import React, { useCallback, useEffect, useState } from "react";
import {
    Autocomplete,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
import type { AnswerSet, AnswerValue } from "../../../../types/answerSet";
import type { Question } from "../../../../types/rubric";
import { fetchAnswerSet, fetchQuestion, fetchQuestionsPaginated } from "../../../../utils/api";

const toNum = (v: unknown): number | undefined => {
    if (v == null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
};

export interface SetAnswerFieldsProps {
    questionId: string | number | undefined;
    answerSetId: string | number | undefined;
    answerId: string | number | undefined;
    onChange: (details: { questionId?: number; answerSetId?: number; answerId?: number }) => void;
    disabled?: boolean;
}

export const SetAnswerFields: React.FC<SetAnswerFieldsProps> = ({
    questionId,
    answerSetId,
    answerId,
    onChange,
    disabled = false,
}) => {
    const [questionSearch, setQuestionSearch] = useState("");
    const [questionOptions, setQuestionOptions] = useState<Question[]>([]);
    const [questionSearchLoading, setQuestionSearchLoading] = useState(false);
    /** True while resolving questionId from saved config (fetchQuestion). Kept separate from search so one finishing does not clear the other. */
    const [questionByIdLoading, setQuestionByIdLoading] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
    const [answerSet, setAnswerSet] = useState<AnswerSet | null>(null);
    const [answerSetLoading, setAnswerSetLoading] = useState(false);

    const qIdNum = toNum(questionId);

    // Load question by id when we have questionId (e.g. from draft) but no selected question
    useEffect(() => {
        if (qIdNum == null) {
            setSelectedQuestion(null);
            setAnswerSet(null);
            setQuestionByIdLoading(false);
            setAnswerSetLoading(false);
            return;
        }
        let cancelled = false;
        if (!selectedQuestion || selectedQuestion.id !== qIdNum) {
            setQuestionByIdLoading(true);
            setAnswerSetLoading(true);
            fetchQuestion(qIdNum)
                .then((q) => {
                    if (!cancelled) {
                        setSelectedQuestion(q);
                        const asId = q.answerSetId != null ? Number(q.answerSetId) : undefined;
                        onChange({ questionId: q.id, answerSetId: asId, answerId: toNum(answerId) });
                        if (q.answers && q.answers.length > 0) {
                            setAnswerSet({ id: asId ?? 0, name: q.answerSetName ?? "", answers: q.answers });
                        } else if (q.answerSetId != null) {
                            return fetchAnswerSet(Number(q.answerSetId)).then((as) => {
                                if (!cancelled) setAnswerSet(as);
                            });
                        }
                    }
                })
                .catch(() => {
                    if (!cancelled) setSelectedQuestion(null);
                })
                .finally(() => {
                    if (!cancelled) {
                        setQuestionByIdLoading(false);
                        setAnswerSetLoading(false);
                    }
                });
        }
        return () => {
            cancelled = true;
        };
    }, [qIdNum]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when questionId from props changes

    // Load answer set when selected question has answerSetId but we don't have answers yet
    useEffect(() => {
        if (!selectedQuestion || answerSet != null) return;
        const asId = selectedQuestion.answerSetId != null ? Number(selectedQuestion.answerSetId) : null;
        if (asId == null) return;
        let cancelled = false;
        setAnswerSetLoading(true);
        fetchAnswerSet(asId)
            .then((as) => {
                if (!cancelled) setAnswerSet(as);
            })
            .catch((err) => {
                if (!cancelled) console.error('Failed to load answer set for question:', err);
            })
            .finally(() => {
                if (!cancelled) setAnswerSetLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedQuestion, answerSet]);

    const fetchQuestions = useCallback(async (search: string) => {
        if (!search.trim()) {
            setQuestionOptions([]);
            return;
        }
        setQuestionSearchLoading(true);
        try {
            const page = await fetchQuestionsPaginated({ search: search.trim(), sortBy: "name", sortDirection: "asc" });
            setQuestionOptions(page.content ?? []);
        } catch {
            setQuestionOptions([]);
        } finally {
            setQuestionSearchLoading(false);
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => fetchQuestions(questionSearch), 300);
        return () => clearTimeout(t);
    }, [questionSearch, fetchQuestions]);

    const handleQuestionChange = (_: unknown, value: Question | null) => {
        setSelectedQuestion(value);
        if (!value) {
            setAnswerSet(null);
            onChange({ questionId: undefined, answerSetId: undefined, answerId: undefined });
            return;
        }
        const qId = value.id;
        const asId = value.answerSetId != null ? Number(value.answerSetId) : undefined;
        onChange({ questionId: qId, answerSetId: asId, answerId: undefined });
        if (value.answers && value.answers.length > 0) {
            setAnswerSet({ id: asId ?? 0, name: value.answerSetName ?? "", answers: value.answers });
        } else if (asId != null) {
            setAnswerSetLoading(true);
            fetchAnswerSet(asId)
                .then((as) => {
                    setAnswerSet(as);
                })
                .catch((err) => {
                    console.error('Failed to load answer set for selected question:', err);
                })
                .finally(() => {
                    setAnswerSetLoading(false);
                });
        } else {
            setAnswerSet(null);
        }
    };

    const answerOptions: AnswerValue[] = answerSet?.answers ?? [];
    const answerIdNum = toNum(answerId);
    const questionFieldLoading = questionSearchLoading || questionByIdLoading;

    const getQuestionLabel = (q: Question) => q.name || q.questionText || `Question ${q.id}`;
    const getAnswerLabel = (a: AnswerValue) => a.displayText ?? a.name ?? a.tagLabel ?? (a.id != null ? String(a.id) : "");

    return (
        <>
            <Autocomplete<Question>
                size="small"
                fullWidth
                loading={questionFieldLoading}
                inputValue={selectedQuestion ? getQuestionLabel(selectedQuestion) : questionSearch}
                onInputChange={(_, value, reason) => {
                    if (reason === "input") setQuestionSearch(value);
                    if (reason === "clear") setQuestionSearch("");
                }}
                value={selectedQuestion}
                onChange={handleQuestionChange}
                options={questionOptions}
                getOptionLabel={getQuestionLabel}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Question"
                        placeholder="Search by name or ID"
                        helperText={questionByIdLoading ? "Loading question…" : undefined}
                    />
                )}
                disabled={disabled}
            />
            {selectedQuestion && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                    {answerSetLoading && (
                        <CircularProgress
                            size={20}
                            sx={{ mt: 0.75 }}
                            aria-label="Loading answers"
                        />
                    )}
                    <FormControl
                        size="small"
                        fullWidth
                        disabled={disabled || answerSetLoading}
                    >
                        <InputLabel id="set-answer-answer-label">Answer</InputLabel>
                        <Select<number | "">
                            labelId="set-answer-answer-label"
                            label="Answer"
                            value={answerIdNum ?? ""}
                            onChange={(e) => {
                                const v = e.target.value;
                                onChange({ answerId: v === "" ? undefined : Number(v) });
                            }}
                            displayEmpty
                            renderValue={(v) => {
                                if (v === "" || v == null) return "";
                                const a = answerOptions.find((x) => x.id != null && Number(x.id) === v);
                                return a ? getAnswerLabel(a) : String(v);
                            }}
                        >
                            <MenuItem value="">
                                <em>{answerSetLoading ? "Loading…" : "Select answer"}</em>
                            </MenuItem>
                            {answerOptions.map((a) => (
                                <MenuItem key={a.id ?? a.name ?? a.tagLabel} value={a.id ?? ""}>
                                    {getAnswerLabel(a)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            )}
        </>
    );
};
